// Lead Generation System — the PRODUCTION LINE. Paste a listing (rich HTML
// paste captures every hyperlink synchronously), extract the fields with one
// click, then advance each prelead down FOUR independent tracks (Outreach /
// NDA / CIM / Interest) one click at a time, in any order. Every credited click
// racks this week's earnings. Phoenix Capital is the Loan Administrator; the
// generator is sourcing and qualifying, not brokering.
//
// Portal rules: no React Query — useWidgetData + useSubmittingMutation only,
// and only /api/portal/* calls.

import { useEffect, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, Link2, Sparkles } from "lucide-react";
import {
  Button,
  Card,
  EmptyState,
  FormField,
  Input,
  SearchField,
  Skeleton,
  StatusBadge,
  Textarea,
  useSubmittingMutation,
} from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import { formatCurrency, formatNumber } from "@/design-system/lib/format";
import { extractLinks, mergeLinks, type CapturedLink } from "@/lib/clipboardLinks";
import { useWidgetData } from "../../dashboard/engine";
import { PortalApiError } from "../../api/client";
import { US_STATES } from "../../lib/usStates";
import {
  leadGeneratorApi,
  type CreatePreleadRequest,
  type LeadGenEarningsSummaryDto,
  type PreleadDetailDto,
  type PreleadListQuery,
  type PreleadSource,
  type PreleadSummaryDto,
} from "../../api/leadGenerator";
import { STATUS_LABEL, STATUS_TONE } from "./pipeline";
import { confirmedMilestones, MILESTONE_FILTER_OPTIONS, preleadDisplayName } from "./preleadStatus";
import { PreleadActionButtons } from "./PreleadActionButtons";
import { PreleadDetailPanel } from "./PreleadDetailPanel";

const PAGE_SIZE = 50;
const MONEY = { minimumFractionDigits: 2, maximumFractionDigits: 2 } as const;

const SELECT_CLASS = cn(
  "h-10 rounded-md border border-border-light bg-white-pure px-space-2 text-small text-onyx",
  "focus:outline-none focus:border-gold focus:shadow-goldRing",
);

const SOURCE_OPTIONS: { value: PreleadSource; label: string }[] = [
  { value: "BizBuySell", label: "BizBuySell" },
  { value: "DealStream", label: "DealStream" },
  { value: "LoopNet", label: "LoopNet" },
  { value: "BrokerWebsite", label: "Broker website" },
  { value: "LinkedIn", label: "LinkedIn" },
  { value: "ColdOutreach", label: "Cold outreach" },
  { value: "Referral", label: "Referral" },
  { value: "Marketplace", label: "Marketplace / other" },
  { value: "Other", label: "Other" },
];

function detailToSummary(d: PreleadDetailDto): PreleadSummaryDto {
  return {
    id: d.id,
    leadGeneratorId: d.leadGeneratorId,
    businessName: d.businessName,
    contactFirstName: d.contactFirstName,
    contactLastName: d.contactLastName,
    phone: d.phone,
    email: d.email,
    city: d.city,
    state: d.state,
    industry: d.industry,
    askingPrice: d.askingPrice,
    cashFlow: d.cashFlow,
    source: d.source,
    status: d.status,
    interestLevel: d.interestLevel,
    ndaStatus: d.ndaStatus,
    cimStatus: d.cimStatus,
    lastContactDate: d.lastContactDate,
    nextFollowUpDate: d.nextFollowUpDate,
    escalatedToLeadId: d.escalatedToLeadId,
    isArchived: d.isArchived,
    createdAt: d.createdAt,
    outreachStatus: d.outreachStatus,
    interestStatus: d.interestStatus,
    confirmedContactedAtUtc: d.confirmedContactedAtUtc,
    confirmedInterestedAtUtc: d.confirmedInterestedAtUtc,
    listingUrl: d.listingUrl,
    rawTextSnippet: d.rawPastedText ?? null,
    // The detail DTO carries no running total; the merge preserves the row's
    // existing total (a fresh insert starts at 0 until the next list refetch).
    totalEarned: 0,
  };
}

export function LeadGenPreleadsTab() {
  // ── Filters + server-side pagination ──────────────────────────────────────
  const [rawSearch, setRawSearch] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  // Multi-select milestone filter (AND semantics) — tokens sent as ?statuses=.
  const [milestones, setMilestones] = useState<string[]>([]);
  const [sourceFilter, setSourceFilter] = useState<PreleadSource | "">("");
  const [stateFilter, setStateFilter] = useState("");
  const [dateField, setDateField] = useState<"created" | "updated">("created");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Debounced text search; resetting the page keeps results on page 1.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedQ(rawSearch.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(t);
  }, [rawSearch]);

  const query: PreleadListQuery = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      q: debouncedQ || undefined,
      statuses: milestones.length > 0 ? milestones : undefined,
      source: sourceFilter || undefined,
      state: stateFilter || undefined,
      dateField,
      // The FE date picker emits bare YYYY-MM-DD (binds to midnight); widen the
      // bounds so a same-day record isn't excluded by the inclusive predicate.
      from: fromDate ? `${fromDate}T00:00:00.000Z` : undefined,
      to: toDate ? `${toDate}T23:59:59.999Z` : undefined,
    }),
    [page, debouncedQ, milestones, sourceFilter, stateFilter, dateField, fromDate, toDate],
  );

  const list = useWidgetData(async () => leadGeneratorApi.listPreleads(query), [query]);
  const earnings = useWidgetData<LeadGenEarningsSummaryDto>(
    async () => leadGeneratorApi.earnings(),
    [],
  );

  const [rows, setRows] = useState<PreleadSummaryDto[]>([]);
  useEffect(() => {
    if (list.data) setRows(list.data.data);
  }, [list.data]);

  const totalCount = list.data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // ── Racking ───────────────────────────────────────────────────────────────
  const [pulseAt, setPulseAt] = useState(0);
  const [optimisticActions, setOptimisticActions] = useState(0);
  useEffect(() => {
    setOptimisticActions(0);
  }, [earnings.data]);
  const rack = () => {
    setOptimisticActions((n) => n + 1);
    setPulseAt(Date.now());
    earnings.reload();
  };

  const upsertFromDetail = (detail: PreleadDetailDto) => {
    const summary = detailToSummary(detail);
    setRows((prev) => {
      const seen = prev.some((r) => r.id === summary.id);
      return seen
        ? // Preserve the row's running earned total — the detail DTO doesn't carry it.
          prev.map((r) => (r.id === summary.id ? { ...summary, totalEarned: r.totalEarned } : r))
        : [summary, ...prev];
    });
  };

  const patchRow = (id: string, patch: Partial<PreleadSummaryDto>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const resetPage = () => setPage(1);

  return (
    <div className="space-y-space-5">
      <RackingChip summary={earnings.data} pulseAt={pulseAt} optimisticActions={optimisticActions} />

      {selectedId ? (
        <PreleadDetailPanel
          preleadId={selectedId}
          onBack={() => setSelectedId(null)}
          onUpdated={upsertFromDetail}
          onRack={rack}
        />
      ) : (
        <>
          <PasteBox onCaptured={upsertFromDetail} onRack={rack} />

          <div className="space-y-space-3">
            <div className="flex items-center justify-between gap-space-3">
              <h2 className="text-h4 font-display font-bold text-onyx m-0">Your preleads</h2>
              {totalCount > 0 && (
                <span className="text-caption text-gray-mid">
                  {formatNumber(totalCount)} total
                </span>
              )}
            </div>

            <FiltersBar
              search={rawSearch}
              onSearch={setRawSearch}
              milestones={milestones}
              onMilestones={(v) => {
                setMilestones(v);
                resetPage();
              }}
              source={sourceFilter}
              onSource={(v) => {
                setSourceFilter(v);
                resetPage();
              }}
              state={stateFilter}
              onState={(v) => {
                setStateFilter(v);
                resetPage();
              }}
              dateField={dateField}
              onDateField={(v) => {
                setDateField(v);
                resetPage();
              }}
              from={fromDate}
              onFrom={(v) => {
                setFromDate(v);
                resetPage();
              }}
              to={toDate}
              onTo={(v) => {
                setToDate(v);
                resetPage();
              }}
            />

            {list.status === "loading" && rows.length === 0 ? (
              <div className="space-y-space-2">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : list.status === "error" && rows.length === 0 ? (
              <Card>
                <p className="text-small text-status-error m-0" role="alert">
                  {list.error ?? "Couldn't load your preleads."}
                </p>
                <Button variant="text" size="sm" onClick={list.reload} className="mt-space-2">
                  Try again
                </Button>
              </Card>
            ) : rows.length === 0 ? (
              <EmptyState
                title="No preleads match"
                description="Paste a business-for-sale listing above, or clear your filters to see your line."
                icon={Sparkles}
              />
            ) : (
              <>
                <ul className="flex flex-col gap-space-2 list-none p-0 m-0">
                  {rows.map((row) => (
                    <PreleadRow
                      key={row.id}
                      row={row}
                      onOpen={setSelectedId}
                      onUpdated={upsertFromDetail}
                      onPatch={patchRow}
                      onRack={rack}
                    />
                  ))}
                </ul>
                {totalPages > 1 && (
                  <Pagination
                    page={page}
                    totalPages={totalPages}
                    onPrev={() => setPage((p) => Math.max(1, p - 1))}
                    onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
                  />
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Racking chip ─────────────────────────────────────────────────────────────

function RackingChip({
  summary,
  pulseAt,
  optimisticActions,
}: {
  summary: LeadGenEarningsSummaryDto | null;
  pulseAt: number;
  optimisticActions: number;
}) {
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (pulseAt === 0) return;
    setFlash(true);
    const t = window.setTimeout(() => setFlash(false), 900);
    return () => window.clearTimeout(t);
  }, [pulseAt]);

  return (
    <Card
      className={cn(
        "flex flex-wrap items-center justify-between gap-space-3 py-space-3",
        "transition-shadow duration-normal ease-standard",
        flash && "ring-2 ring-gold",
      )}
      data-testid="leadgen-racking"
    >
      <div className="flex items-center gap-space-2">
        <Sparkles size={18} className="text-gold-shadow" aria-hidden />
        <span className="text-small text-gray-mid">This week you&apos;ve racked</span>
      </div>
      <div className="flex items-baseline gap-space-2">
        <span className="text-h3 font-display font-bold text-status-success m-0">
          {summary ? formatCurrency(summary.totalCredited, MONEY) : "—"}
        </span>
        {summary && (
          <span className="text-caption text-gray-mid">
            across {formatNumber(summary.totalCount + optimisticActions)} action
            {summary.totalCount + optimisticActions === 1 ? "" : "s"}
          </span>
        )}
      </div>
    </Card>
  );
}

// ── Filters bar ──────────────────────────────────────────────────────────────

function FiltersBar({
  search,
  onSearch,
  milestones,
  onMilestones,
  source,
  onSource,
  state,
  onState,
  dateField,
  onDateField,
  from,
  onFrom,
  to,
  onTo,
}: {
  search: string;
  onSearch: (v: string) => void;
  milestones: string[];
  onMilestones: (v: string[]) => void;
  source: PreleadSource | "";
  onSource: (v: PreleadSource | "") => void;
  state: string;
  onState: (v: string) => void;
  dateField: "created" | "updated";
  onDateField: (v: "created" | "updated") => void;
  from: string;
  onFrom: (v: string) => void;
  to: string;
  onTo: (v: string) => void;
}) {
  return (
    <div className="space-y-space-3">
      <SearchField
        value={search}
        onChange={onSearch}
        placeholder="Search business, contact, email, phone, listing URL…"
        label="Search preleads"
      />
      <MilestoneFilter selected={milestones} onChange={onMilestones} />
      <div className="flex flex-wrap items-center gap-space-2">
        <select
          className={SELECT_CLASS}
          value={source}
          onChange={(e) => onSource(e.target.value as PreleadSource | "")}
          aria-label="Source filter"
        >
          <option value="">All sources</option>
          {SOURCE_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <select
          className={SELECT_CLASS}
          value={state}
          onChange={(e) => onState(e.target.value)}
          aria-label="State filter"
        >
          <option value="">All states</option>
          {US_STATES.map((s) => (
            <option key={s.code} value={s.code}>
              {s.name} ({s.code})
            </option>
          ))}
        </select>
        <select
          className={SELECT_CLASS}
          value={dateField}
          onChange={(e) => onDateField(e.target.value as "created" | "updated")}
          aria-label="Date field"
        >
          <option value="created">Created</option>
          <option value="updated">Updated</option>
        </select>
        <input
          type="date"
          className={SELECT_CLASS}
          value={from}
          onChange={(e) => onFrom(e.target.value)}
          aria-label="From date"
        />
        <input
          type="date"
          className={SELECT_CLASS}
          value={to}
          onChange={(e) => onTo(e.target.value)}
          aria-label="To date"
        />
      </div>
    </div>
  );
}

// Multi-select milestone filter — AND semantics (a prelead matches only when it
// has reached EVERY selected milestone). Portal-local (the staff FilterChipGroup
// lives under @/components and is out of the portal's import allow-list).
function MilestoneFilter({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const toggle = (value: string) => {
    const set = new Set(selected);
    if (set.has(value)) set.delete(value);
    else set.add(value);
    onChange(Array.from(set));
  };
  return (
    <div className="space-y-space-1" role="group" aria-label="Milestone filter">
      <div className="flex items-center justify-between gap-space-2">
        <span className="text-caption uppercase tracking-wide text-gray-mid">
          Milestones reached (all selected)
        </span>
        {selected.length > 0 && (
          <Button variant="text" size="sm" onClick={() => onChange([])}>
            Clear
          </Button>
        )}
      </div>
      <div className="flex flex-wrap gap-space-2">
        {MILESTONE_FILTER_OPTIONS.map((opt) => {
          const active = selected.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              aria-pressed={active}
              onClick={() => toggle(opt.value)}
              data-testid={`leadgen-milestone-${opt.value}`}
              className={cn(
                "inline-flex items-center gap-space-1 rounded-pill border px-space-3 py-[5px] text-caption font-medium",
                "transition-colors duration-fast ease-standard focus-visible:outline-none focus-visible:shadow-goldRing",
                active
                  ? "border-gold bg-gold text-onyx"
                  : "border-border-light bg-white-pure text-gray-mid hover:border-gold hover:text-onyx",
              )}
            >
              {active && <CheckCircle2 size={12} aria-hidden />}
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Pagination({
  page,
  totalPages,
  onPrev,
  onNext,
}: {
  page: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-space-3 pt-space-2">
      <Button variant="secondary" size="sm" onClick={onPrev} disabled={page <= 1}>
        Previous
      </Button>
      <span className="text-caption text-gray-mid">
        Page {page} of {totalPages}
      </span>
      <Button variant="secondary" size="sm" onClick={onNext} disabled={page >= totalPages}>
        Next
      </Button>
    </div>
  );
}

// ── Paste box ────────────────────────────────────────────────────────────────

// Rich-paste link capture lives in @/lib/clipboardLinks, shared with the staff
// Paste-to-create-Lead modal. This surface opts INTO the boilerplate denylist so
// the composer preview isn't flooded with a listing site's chrome; the SERVER
// still runs the authoritative listing-pertinence filter over capturedLinksJson.

function PasteBox({
  onCaptured,
  onRack,
}: {
  onCaptured: (detail: PreleadDetailDto) => void;
  onRack: () => void;
}) {
  const [text, setText] = useState("");
  const [links, setLinks] = useState<CapturedLink[]>([]);
  const [source, setSource] = useState<PreleadSource>("Marketplace");
  const [manualOpen, setManualOpen] = useState(false);
  const [fallbackNote, setFallbackNote] = useState<string | null>(null);

  const extract = useSubmittingMutation(
    () =>
      leadGeneratorApi.extract({
        rawText: text.trim(),
        capturedLinksJson: links.length > 0 ? JSON.stringify(links) : null,
        source,
      }),
    {
      onSuccess: (detail) => {
        onCaptured(detail);
        onRack();
        setText("");
        setLinks([]);
        setFallbackNote(null);
      },
      onError: (err) => {
        // Render the SERVER's 422 message: the same status is raised both when
        // the AI couldn't read the text AND when the daily AI cost ceiling is
        // reached, and those read completely differently to the generator
        // waiting on it. Fixed copy only when the server sent none.
        if (err instanceof PortalApiError && err.status === 422) {
          setFallbackNote(
            err.message.trim() ||
              "We couldn't read that automatically. Add the details manually below — your text is kept.",
          );
          setManualOpen(true);
        }
      },
    },
  );

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const html = e.clipboardData.getData("text/html");
    const plain = e.clipboardData.getData("text/plain");
    const found = extractLinks(html, plain, { dropBoilerplate: true });
    if (found.length > 0) setLinks((prev) => mergeLinks(prev, found));
  };

  const nonFatalError =
    extract.error instanceof PortalApiError && extract.error.status === 422
      ? null
      : extract.error
        ? extract.error instanceof Error
          ? extract.error.message
          : "Something went wrong. Try again."
        : null;

  return (
    <Card featured className="space-y-space-3" data-testid="leadgen-paste">
      <div className="flex items-center justify-between gap-space-3">
        <div>
          <h2 className="text-h4 font-display font-bold text-onyx m-0">New prelead</h2>
          <p className="text-caption text-gray-mid m-0">
            Paste a listing or a message thread — we&apos;ll pull out the business and contact
            details for you.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setManualOpen((v) => !v)}
          data-testid="leadgen-paste-manual-toggle"
        >
          {manualOpen ? "Hide manual entry" : "Add manually"}
        </Button>
      </div>

      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onPaste={handlePaste}
        rows={6}
        placeholder="Paste here — links and formatting are captured automatically."
        data-testid="leadgen-paste-input"
        aria-label="Paste listing or thread text"
      />

      {links.length > 0 && (
        <div className="space-y-space-1" data-testid="leadgen-paste-links">
          <p className="text-caption uppercase tracking-wide text-gray-mid m-0">
            Captured links ({links.length})
          </p>
          <ul className="flex flex-wrap gap-space-2 list-none p-0 m-0">
            {links.map((l) => (
              <li
                key={l.href}
                className="inline-flex max-w-full items-center gap-space-1 rounded-pill border border-border-light bg-white-warm px-space-2 py-[2px] text-caption text-onyx"
                title={l.href}
              >
                <Link2 size={12} className="shrink-0 text-gold-shadow" aria-hidden />
                <span className="truncate">{l.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-space-3">
        <label className="flex flex-col gap-space-1">
          <span className="text-label font-medium text-onyx">Source</span>
          <SourceSelect value={source} onChange={setSource} />
        </label>
        <Button
          variant="primary"
          onClick={() => void extract.submit()}
          loading={extract.pending}
          disabled={text.trim().length === 0}
          data-testid="leadgen-paste-extract"
        >
          Extract
        </Button>
      </div>

      {nonFatalError && (
        <p className="flex items-center gap-space-1 text-small text-status-error m-0" role="alert">
          <AlertCircle size={14} aria-hidden /> {nonFatalError}
        </p>
      )}
      {fallbackNote && (
        <p
          className="flex items-center gap-space-1 text-small text-status-warning m-0"
          role="alert"
          data-testid="leadgen-paste-fallback"
        >
          <AlertCircle size={14} aria-hidden /> {fallbackNote}
        </p>
      )}

      {manualOpen && (
        <ManualAddForm
          defaultSource={source}
          seedText={text.trim() || null}
          onCreated={(detail) => {
            onCaptured(detail);
            onRack();
            setText("");
            setLinks([]);
            setManualOpen(false);
            setFallbackNote(null);
          }}
        />
      )}
    </Card>
  );
}

function SourceSelect({
  value,
  onChange,
}: {
  value: PreleadSource;
  onChange: (v: PreleadSource) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as PreleadSource)}
      data-testid="leadgen-source-select"
      className={cn(
        "h-10 px-space-3 font-body text-body sm:text-small text-onyx bg-white-pure",
        "border border-border-light rounded-md",
        "focus:outline-none focus:border-gold focus:shadow-goldRing",
      )}
    >
      {SOURCE_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

// ── Manual add fallback ──────────────────────────────────────────────────────

function ManualAddForm({
  defaultSource,
  seedText,
  onCreated,
}: {
  defaultSource: PreleadSource;
  seedText: string | null;
  onCreated: (detail: PreleadDetailDto) => void;
}) {
  const [form, setForm] = useState<CreatePreleadRequest>({ source: defaultSource });
  const set = (patch: Partial<CreatePreleadRequest>) => setForm((f) => ({ ...f, ...patch }));

  const create = useSubmittingMutation(
    () =>
      leadGeneratorApi.createManual({
        ...form,
        source: form.source ?? defaultSource,
        notes: seedText ?? undefined,
        askingPrice:
          form.askingPrice != null && !Number.isNaN(form.askingPrice) ? form.askingPrice : undefined,
      }),
    { onSuccess: (detail) => onCreated(detail) },
  );

  const canSave =
    (form.businessName?.trim().length ?? 0) > 0 ||
    (form.contactFirstName?.trim().length ?? 0) > 0 ||
    (form.contactLastName?.trim().length ?? 0) > 0;

  return (
    <div
      className="space-y-space-3 rounded-md border border-border-light bg-white-warm p-space-3"
      data-testid="leadgen-manual-form"
    >
      <p className="text-small font-semibold text-onyx m-0">Add the details yourself</p>
      <div className="grid grid-cols-1 gap-space-3 sm:grid-cols-2">
        <FormField label="Business name">
          <Input
            value={form.businessName ?? ""}
            onChange={(e) => set({ businessName: e.target.value })}
            placeholder="ACME Plumbing"
          />
        </FormField>
        <FormField label="Industry">
          <Input
            value={form.industry ?? ""}
            onChange={(e) => set({ industry: e.target.value })}
            placeholder="Plumbing"
          />
        </FormField>
        <FormField label="Contact first name">
          <Input
            value={form.contactFirstName ?? ""}
            onChange={(e) => set({ contactFirstName: e.target.value })}
          />
        </FormField>
        <FormField label="Contact last name">
          <Input
            value={form.contactLastName ?? ""}
            onChange={(e) => set({ contactLastName: e.target.value })}
          />
        </FormField>
        <FormField label="Email">
          <Input type="email" value={form.email ?? ""} onChange={(e) => set({ email: e.target.value })} />
        </FormField>
        <FormField label="Phone">
          <Input value={form.phone ?? ""} onChange={(e) => set({ phone: e.target.value })} />
        </FormField>
        <FormField label="City">
          <Input value={form.city ?? ""} onChange={(e) => set({ city: e.target.value })} />
        </FormField>
        <FormField label="State">
          <Input value={form.state ?? ""} onChange={(e) => set({ state: e.target.value })} placeholder="AZ" />
        </FormField>
        <FormField label="Asking price">
          <Input
            type="number"
            inputMode="numeric"
            value={form.askingPrice ?? ""}
            onChange={(e) => set({ askingPrice: e.target.value === "" ? null : Number(e.target.value) })}
            placeholder="450000"
          />
        </FormField>
        <FormField label="Listing broker (source)">
          <Input value={form.brokerName ?? ""} onChange={(e) => set({ brokerName: e.target.value })} />
        </FormField>
        <FormField label="Listing broker company">
          <Input
            value={form.brokerCompany ?? ""}
            onChange={(e) => set({ brokerCompany: e.target.value })}
          />
        </FormField>
        <FormField label="Listing URL">
          <Input value={form.listingUrl ?? ""} onChange={(e) => set({ listingUrl: e.target.value })} />
        </FormField>
      </div>

      {create.error != null && (
        <p className="text-small text-status-error m-0" role="alert">
          {create.error instanceof Error ? create.error.message : "Couldn't save. Try again."}
        </p>
      )}
      <Button
        variant="primary"
        size="sm"
        onClick={() => void create.submit()}
        loading={create.pending}
        disabled={!canSave}
        data-testid="leadgen-manual-save"
      >
        Add prelead
      </Button>
    </div>
  );
}

// ── Production-line row ───────────────────────────────────────────────────────

function preleadName(row: PreleadSummaryDto): string {
  return preleadDisplayName({
    businessName: row.businessName,
    contactFirstName: row.contactFirstName,
    contactLastName: row.contactLastName,
    rawText: row.rawTextSnippet,
    listingUrl: row.listingUrl,
    createdAt: row.createdAt,
  });
}

function preleadMeta(row: PreleadSummaryDto): string {
  const parts = [
    [row.city, row.state].filter(Boolean).join(", "),
    row.industry ?? "",
    row.askingPrice != null ? formatCurrency(row.askingPrice, MONEY) : "",
  ].filter((p) => p.length > 0);
  return parts.join(" · ");
}

function PreleadRow({
  row,
  onOpen,
  onUpdated,
  onPatch,
  onRack,
}: {
  row: PreleadSummaryDto;
  onOpen: (id: string) => void;
  onUpdated: (detail: PreleadDetailDto) => void;
  onPatch: (id: string, patch: Partial<PreleadSummaryDto>) => void;
  onRack: () => void;
}) {
  return (
    <li>
      <Card className="space-y-space-2 py-space-3" data-testid={`leadgen-row-${row.id}`}>
        <div className="flex flex-wrap items-center justify-between gap-space-3">
          <button
            type="button"
            onClick={() => onOpen(row.id)}
            className="min-w-0 text-left"
            data-testid={`leadgen-open-${row.id}`}
          >
            <p className="text-small font-semibold text-onyx m-0 truncate hover:text-gold-shadow">
              {preleadName(row)}
            </p>
            {preleadMeta(row) && (
              <p className="text-caption text-gray-mid m-0 truncate">{preleadMeta(row)}</p>
            )}
          </button>
          <div className="flex flex-wrap items-center justify-end gap-space-2">
            <span
              className="text-caption font-semibold tabular-nums text-status-success"
              data-testid={`leadgen-earned-${row.id}`}
            >
              {formatCurrency(row.totalEarned, MONEY)} earned
            </span>
            {confirmedMilestones(row).map((c) => (
              <StatusBadge key={c.key} tone="ok" data-testid={`leadgen-confirmed-${c.key}-${row.id}`}>
                <CheckCircle2 size={12} aria-hidden /> {c.label}
              </StatusBadge>
            ))}
            <StatusBadge tone={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status]}</StatusBadge>
          </div>
        </div>

        <PreleadActionButtons
          prelead={row}
          size="sm"
          enableFileDrops
          onUpdated={onUpdated}
          onEscalated={(leadId) =>
            onPatch(row.id, { status: "Escalated", escalatedToLeadId: leadId })
          }
          onRack={onRack}
        />
      </Card>
    </li>
  );
}
